// Mini-App image-search session storage helpers — a port of
// apps/web/lib/image-search-session.ts.
//
// The picker stashes the cropped image base64 under an opaque per-search token
// in sessionStorage, then navigates to /catalog?imageSearch=<token>. The catalog
// reads it back by token and uploads it *per channel* there — so the navigation
// is never blocked by a slow/failing upload (web issue #118), and each channel
// tab uploads independently (one channel's upstream failure can't poison others).

/** sessionStorage key holding the most recent image-search payload. */
export const SESSION_KEY = "kk:image-search";

export interface SessionRegionBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface SearchSourceImage {
  dataUrl: string;
  width?: number;
  height?: number;
}

export interface SearchDetectContext {
  channel: "1688";
  detectImageId?: string;
  regionList?: SessionRegionBox[];
  currentRegion?: SessionRegionBox | null;
}

export interface SearchSessionContext {
  sourceImage?: SearchSourceImage;
  detectContext?: SearchDetectContext;
}

export interface SessionSearchImage {
  token: string;
  base64: string;
  sourceImage?: SearchSourceImage;
  detectContext?: SearchDetectContext;
  updatedAt?: number;
}

/** Persist the picked image so the catalog page can read it back by token. */
export function stashSearchImage(base64: string, context: SearchSessionContext = {}): string {
  const token = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
  try {
    const payload: SessionSearchImage = { token, base64, updatedAt: Date.now() };
    if (context.sourceImage) payload.sourceImage = context.sourceImage;
    if (context.detectContext) payload.detectContext = context.detectContext;
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(payload));
  } catch {
    // sessionStorage can throw (private mode / quota). Fall through with the
    // token anyway; catalog falls back to its empty state if it can't read.
  }
  return token;
}

/**
 * Drop the stashed image. Called when the user switches back to a keyword
 * search — keyword and image search are mutually exclusive, so the later action
 * clears the earlier one's payload so a stale base64 can never re-surface a
 * phantom image-search state.
 */
export function clearSearchImage(): void {
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    // Best-effort: sessionStorage can throw (private mode).
  }
}

/** Read the full stashed image-search payload iff it matches `token`. */
export function readSearchImage(token: string): SessionSearchImage | null {
  if (!token) return null;
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<SessionSearchImage>;
    if (parsed.token !== token || typeof parsed.base64 !== "string") return null;
    const payload: SessionSearchImage = { token: parsed.token, base64: parsed.base64 };
    const sourceImage = normalizeSourceImage(parsed.sourceImage);
    const detectContext = normalizeDetectContext(parsed.detectContext);
    if (sourceImage) payload.sourceImage = sourceImage;
    if (detectContext) payload.detectContext = detectContext;
    if (typeof parsed.updatedAt === "number") payload.updatedAt = parsed.updatedAt;
    return payload;
  } catch {
    return null;
  }
}

/** Read the stashed base64 iff it matches `token` (one image at a time). */
export function readSearchImageBase64(token: string): string | null {
  return readSearchImage(token)?.base64 ?? null;
}

function normalizeSourceImage(value: unknown): SearchSourceImage | undefined {
  if (!value || typeof value !== "object") return undefined;
  const source = value as Partial<SearchSourceImage>;
  if (typeof source.dataUrl !== "string" || !source.dataUrl.startsWith("data:image/")) {
    return undefined;
  }
  const normalized: SearchSourceImage = { dataUrl: source.dataUrl };
  if (typeof source.width === "number") normalized.width = source.width;
  if (typeof source.height === "number") normalized.height = source.height;
  return normalized;
}

function normalizeRegion(value: unknown): SessionRegionBox | null {
  if (!value || typeof value !== "object") return null;
  const r = value as Partial<SessionRegionBox>;
  return typeof r.x === "number" &&
    typeof r.y === "number" &&
    typeof r.w === "number" &&
    typeof r.h === "number"
    ? { x: r.x, y: r.y, w: r.w, h: r.h }
    : null;
}

function normalizeDetectContext(value: unknown): SearchDetectContext | undefined {
  if (!value || typeof value !== "object") return undefined;
  const context = value as Partial<SearchDetectContext>;
  if (context.channel !== "1688") return undefined;
  const regionList = Array.isArray(context.regionList)
    ? context.regionList.flatMap((r) => {
        const normalized = normalizeRegion(r);
        return normalized ? [normalized] : [];
      })
    : undefined;
  const normalized: SearchDetectContext = { channel: "1688" };
  if (typeof context.detectImageId === "string") normalized.detectImageId = context.detectImageId;
  if (regionList) normalized.regionList = regionList;
  normalized.currentRegion = normalizeRegion(context.currentRegion);
  return normalized;
}
